import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Search, ExternalLink, FileText } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AGENCIES = ["ALL", "FBI", "NSA", "USAF", "DoD", "CIA", "NASA"];

const AgencyChip = ({ agency }) => {
  const colors = {
    FBI:   "text-[#3e5c99] border-[#3e5c99]",
    NSA:   "text-[#6a5acd] border-[#6a5acd]",
    USAF:  "text-[#76b1c9] border-[#76b1c9]",
    DoD:   "text-[#9fbf5f] border-[#9fbf5f]",
    CIA:   "text-[#c8b26f] border-[#c8b26f]",
    NASA:  "text-[#ff6a2a] border-[#ff6a2a]",
  };
  return <span className={`agency-badge ${colors[agency] || 'text-amber border-amber'}`}>{agency}</span>;
};

export default function VaultPage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [agency, setAgency] = useState("ALL");

  useEffect(() => {
    axios.get(`${API}/vault/files`).then(r => {
      setFiles(r.data || []);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    return files.filter(f => {
      if (agency !== "ALL" && f.agency !== agency) return false;
      if (!query) return true;
      const q = query.toLowerCase();
      return (
        f.title.toLowerCase().includes(q) ||
        f.summary.toLowerCase().includes(q) ||
        f.tags.some(t => t.toLowerCase().includes(q)) ||
        (f.case_number || "").toLowerCase().includes(q)
      );
    });
  }, [files, query, agency]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-14" data-testid="vault-page">
      <div className="flex items-center gap-4 flex-wrap">
        <span className="redacted-stamp">FOR PUBLIC REVIEW</span>
        <span className="font-mono text-xs tracking-[0.3em] text-white/80">UAP VAULT · {files.length} CASE FILES</span>
      </div>
      <h1 className="font-neon text-5xl md:text-7xl text-white glow-white mt-5">
        <span className="font-mono font-extrabold mr-2">$</span>THE VAULT
      </h1>
      <p className="font-type text-white text-lg mt-2 max-w-3xl">
        A curated catalog of declassified and publicly-disclosed UFO/UAP case files from the
        United States government. Every card links directly to the primary source.
      </p>

      {/* Controls */}
      <div className="mt-10 grid md:grid-cols-12 gap-4 items-center">
        <div className="md:col-span-6 relative">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-dust" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="search cases, tags, case #..."
            className="p-input pl-11"
            data-testid="vault-search"
          />
        </div>
        <div className="md:col-span-6 flex flex-wrap gap-2">
          {AGENCIES.map(a => (
            <button
              key={a}
              onClick={() => setAgency(a)}
              className={`agency-badge transition-colors ${agency===a ? 'bg-white text-ink border-white' : 'text-white border-white hover:bg-white hover:text-ink'}`}
              data-testid={`agency-filter-${a.toLowerCase()}`}
            >
              {a}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="font-crt text-white text-2xl mt-16">loading archives<span className="dotdotdot"><span>.</span><span>.</span><span>.</span></span></div>
      ) : (
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-7" data-testid="vault-grid">
          {filtered.map((f, i) => (
            <a
              key={f.id}
              href={f.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="paper-card px-6 pt-8 pb-6 block hover:scale-[1.01] transition-transform"
              style={{ transform: `rotate(${i%3===0?'-0.3deg': i%3===1?'0.4deg':'-0.1deg'})` }}
              data-testid={`vault-card-${f.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <AgencyChip agency={f.agency} />
                <span className="font-mono text-[10px] text-ink/60 tracking-[0.2em]">{f.year}</span>
              </div>

              <h3 className="font-type text-ink text-2xl mt-4 leading-tight">{f.title}</h3>

              <div className="font-mono text-[10px] text-ink/60 tracking-[0.2em] mt-1">
                CASE #{f.case_number || "—"} · {f.classification}
              </div>

              <div className="dashed-div my-4" />

              <p className="font-body text-ink/90 text-sm leading-relaxed">{f.summary}</p>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {f.tags.map(t => (
                  <span key={t} className="font-mono text-[10px] text-ink/70 border border-ink/20 px-1.5 py-0.5">#{t}</span>
                ))}
              </div>

              <div className="mt-5 flex items-center justify-between">
                <span className="font-mono text-[11px] text-ink/60 tracking-[0.2em]">PRIMARY SOURCE</span>
                <span className="inline-flex items-center gap-1 font-mono text-xs text-ink">
                  OPEN <ExternalLink size={12} />
                </span>
              </div>
            </a>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full font-type text-white text-xl py-16 flex items-center gap-3">
              <FileText size={22} /> No files match that query. Try fewer words, Earthling.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
